import React, { useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import Modal from './Modal';

const DISPUTE_REASONS = [
  { value: 'incorrect_amount', label: 'Incorrect Amount Charged' },
  { value: 'service_issue', label: 'Service Quality Issue' },
  { value: 'duplicate_charge', label: 'Duplicate Charge' },
  { value: 'unauthorized_charge', label: 'Unauthorized Charge' },
  { value: 'other', label: 'Other' }
];

const DisputeModal = ({ isOpen, onClose, onSubmit, invoice }) => {
    const [reason, setReason] = useState('');
    const [comments, setComments] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
  
    const handleSubmit = async (e) => {
      e.preventDefault();
      
      if (!reason) {
        setError('Please select a dispute reason');
        return;
      }
      if (!comments.trim()) {
        setError('Please provide detailed comments');
        return;
      }
  
      setIsSubmitting(true);
      setError(null);
  
      try {
        await onSubmit({
          action: 'dispute_charges',
          reason,
          comments
        });
        
        toast.success('Dispute submitted successfully');
        onClose();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to submit dispute');
        toast.error('Failed to submit dispute');
      } finally {
        setIsSubmitting(false);
      }
    };
  
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Dispute Charges"
      >
        {error && (
          <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          </div>
        )}
  
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dispute Reason
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="block w-full px-3 py-2 rounded-lg border border-gray-300 
                shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              required
            >
              <option value="">Select a reason</option>
              {DISPUTE_REASONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
  
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Detailed Comments
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={4}
              className="block w-full px-3 py-2 rounded-lg border border-gray-300 
                shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              placeholder="Please provide detailed information about your dispute..."
              required
            />
          </div>
  
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`
                px-4 py-2 rounded-lg font-medium text-sm
                inline-flex items-center
                ${isSubmitting ? 'bg-blue-100 text-blue-600' : 'bg-blue-600 text-white hover:bg-blue-700'}
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
            >
              {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Submit Dispute
            </button>
          </div>
        </form>
      </Modal>
    );
};

export default DisputeModal; 